import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
  Alert,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import DataTable from '../components/DataTable';
import { Permission, Column, PaginationParams } from '../types';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { permissionsService } from '../services/permissionsService';
import { rolesService } from '../services/rolesService';
import { mapApiPermissionToUi, mapUiToCreatePermissionApiModel, mapUiToUpdatePermissionApiModelPatch } from '../utils/permissionsMapper';
import { PERMISSIONS } from '../utils/permissionAccess';

interface PermissionFormData {
  name: string;
  description: string;
  roles: string[];
}

const Permissions: React.FC = () => {
  const { permissions, setPermissions } = useData();
  const { tokens, login, user } = useAuth();
  
  // Get user permissions
  const userPermissions = user?.permissions || [];
  
  // Helper function to check if user has a specific permission
  const hasPermission = (permission: string): boolean => {
    return userPermissions.some(userPerm => 
      userPerm.toLowerCase() === permission.toLowerCase() ||
      userPerm === permission
    );
  };
  
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'info' | 'warning' }>({
    open: false,
    message: '',
    severity: 'info',
  });
  const [pagination] = useState<PaginationParams>({
    page: 1,
    pageSize: 10,
  });
  const [roles, setRoles] = useState<any[]>([]);

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [editingPermission, setEditingPermission] = useState<Permission | null>(null);
  const [formData, setFormData] = useState<PermissionFormData>({
    name: '',
    description: '',
    roles: [],
  });

  // Initialize services and load data
  useEffect(() => {
    if (tokens && login) {
      permissionsService.initialize(tokens, login);
      rolesService.initialize(tokens, login);
      loadPermissions();
      loadRoles(); // Load roles from API
    }
  }, [tokens, login]);

  const loadPermissions = async (params: PaginationParams = pagination) => {
    try {
      setLoading(true);
      setError(null);
      
      // Check if permissions endpoints are available
      if (!permissionsService.isAuthenticated()) {
        setError('Authentication required. Please log in.');
        setLoading(false);
        return;
      }

      // Call the actual API endpoint
      const response = await permissionsService.getPermissions(params);
      
      if (response.succeeded) {
        // Map permissions - roles are already included in the API response
        const mappedPermissions = response.data.items.map((permission) => mapApiPermissionToUi(permission));
        setPermissions(mappedPermissions);
      } else {
        setError(response.errorDescription || 'Failed to load permissions');
      }
    } catch (err) {
      console.error('Error loading permissions:', err);
      setError('Failed to load permissions. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      // Check if roles service is authenticated
      if (!rolesService.isAuthenticated()) {
        console.warn('Roles service not authenticated');
        setRoles([]);
        return;
      }

      // Load roles from the API
      const response = await rolesService.getRoles({ page: 1, pageSize: 100 });
      
      if (response.succeeded && response.data) {
        const rolesData = response.data.items || [];
        // Map API roles to the format expected by the dropdown
        const mappedRoles = rolesData.map((role: any) => ({
          id: role.id,
          name: role.name
        }));
        setRoles(mappedRoles);
      } else {
        console.error('Failed to load roles:', response.errorDescription);
        setRoles([]);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
      setRoles([]);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get role name for display in forms (fallback to ID if name not found)
  const getRoleName = (roleId: string): string => {
    const role = roles.find(r => r.id === roleId);
    return role ? role.name : roleId;
  };

  const columns: Column[] = [
    { id: 'name', label: 'Name', minWidth: 200, width: '30%', sortable: true },
    { id: 'description', label: 'Description', minWidth: 300, width: '40%', sortable: true },
    { 
      id: 'roles', 
      label: 'Linked Roles', 
      minWidth: 200, 
      width: '30%',
      sortable: false,
      render: (_value: string[], row: Permission) => (
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {(row.roleNames || []).map((roleName) => (
            <Chip key={roleName} label={roleName} size="small" color="primary" />
          ))}
        </Box>
      ),
    },
  ];

  const handleAdd = (): void => {
    setEditingPermission(null);
    setFormData({
      name: '',
      description: '',
      roles: [],
    });
    setOpenModal(true);
  };

  const handleEdit = async (permission: Permission): Promise<void> => {
    // Fetch fresh role data for this permission
    // Use permissionId or id (they should be the same, but use permissionId as it's the API identifier)
    const permissionIdToUse = permission.permissionId || permission.id;
    
    try {
      console.log(`🔐 Fetching fresh roles for permission: ${permission.name} (${permissionIdToUse})`);
      const rolesResponse = await permissionsService.getPermissionRoles(permissionIdToUse);
      
      let currentRoles: string[] = [];
      let currentRoleNames: string[] = [];
      
      if (rolesResponse.succeeded && rolesResponse.data) {
        // rolesResponse.data is already an array, not an object with items
        currentRoles = rolesResponse.data.map((role: any) => role.id);
        currentRoleNames = rolesResponse.data.map((role: any) => role.name);
        console.log(`🔐 Fresh roles for ${permission.name}:`, currentRoleNames);
      } else {
        console.log(`🔐 No fresh roles found for ${permission.name}`);
        // If no roles found, use existing data from permission object
        currentRoles = permission.roles || [];
        currentRoleNames = permission.roleNames || [];
      }
      
      // Update the permission object with fresh data
      const updatedPermission = {
        ...permission,
        roles: currentRoles,
        roleNames: currentRoleNames
      };
      
      setEditingPermission(updatedPermission);
      setFormData({
        name: permission.name,
        description: permission.description,
        roles: currentRoles,
      });
    } catch (error) {
      console.error(`🔐 Error fetching fresh roles for ${permission.name}:`, error);
      // Fallback to existing data if fetch fails - ensure editingPermission is set with fallback data
      const fallbackPermission = {
        ...permission,
        roles: permission.roles || [],
        roleNames: permission.roleNames || []
      };
      setEditingPermission(fallbackPermission);
      setFormData({
        name: permission.name,
        description: permission.description,
        roles: permission.roles || [],
      });
    }
    
    setOpenModal(true);
  };

  const handleDelete = async (permission: Permission): Promise<void> => {
    try {
      setLoading(true);
      const response = await permissionsService.deletePermission(permission.id);
      
      if (response.succeeded) {
        showSnackbar('Permission deleted successfully', 'success');
        loadPermissions(); // Reload the list
      } else {
        showSnackbar(response.errorDescription || 'Failed to delete permission', 'error');
      }
    } catch (err) {
      console.error('Error deleting permission:', err);
      showSnackbar('Failed to delete permission. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (): Promise<void> => {
    try {
      if (editingPermission) {
        // Update existing permission
        const updateData = mapUiToUpdatePermissionApiModelPatch(editingPermission, formData);
        
        // Check if there are any property changes (name or description)
        const hasPropertyChanges = updateData.name !== undefined || updateData.description !== undefined;
        
        // Check if there are role changes
        // Use the roles from editingPermission (which should have fresh data from handleEdit)
        const currentRoleIds = (editingPermission.roles || []).slice().sort();
        const newRoleIds = (formData.roles || []).slice().sort();
        
        // Deep comparison: check if arrays contain the same elements (order doesn't matter)
        const hasRoleChanges = 
          currentRoleIds.length !== newRoleIds.length ||
          !currentRoleIds.every((id, index) => id === newRoleIds[index]);
        
        if (!hasPropertyChanges && !hasRoleChanges) {
          showSnackbar('No changes detected', 'info');
          setOpenModal(false);
          return;
        }
        
        // Update permission properties (name/description) if changed
        if (hasPropertyChanges) {
          const response = await permissionsService.updatePermission(editingPermission.id, updateData);
          
          if (!response.succeeded) {
            showSnackbar(response.errorDescription || 'Failed to update permission', 'error');
            return;
          }
        }
        
        // Update role assignments if changed
        if (hasRoleChanges) {
          try {
            // Use unsorted arrays for comparison to calculate additions/removals correctly
            const currentRoleIdsUnsorted = editingPermission.roles || [];
            const newRoleIdsUnsorted = formData.roles || [];
            
            const toAdd = newRoleIdsUnsorted.filter(id => !currentRoleIdsUnsorted.includes(id));
            const toRemove = currentRoleIdsUnsorted.filter(id => !newRoleIdsUnsorted.includes(id));
            
            console.log('🔐 Permission role change calculation:', {
              currentRoleIds: currentRoleIdsUnsorted,
              newRoleIds: newRoleIdsUnsorted,
              toAdd,
              toRemove
            });
            
            if (toAdd.length > 0 || toRemove.length > 0) {
              // Use permissionId or id (they should be the same, but use permissionId as it's the API identifier)
              const permissionIdToUse = editingPermission.permissionId || editingPermission.id;
              
              console.log('🔐 Updating role assignments for permission:', {
                permissionId: permissionIdToUse,
                toAdd,
                toRemove
              });
              
              const validToAdd = toAdd.filter(id => typeof id === 'string' && id.trim() !== '');
              const validToRemove = toRemove.filter(id => typeof id === 'string' && id.trim() !== '');
              
              const rolesResponse = await permissionsService.updatePermissionRoles(permissionIdToUse, {
                add: validToAdd,
                remove: validToRemove
              });
              
              if (!rolesResponse.succeeded) {
                showSnackbar(
                  hasPropertyChanges 
                    ? 'Permission updated but failed to update role assignments' 
                    : rolesResponse.errorDescription || 'Failed to update role assignments',
                  'warning'
                );
              }
            }
          } catch (roleErr) {
            console.error('Error updating role assignments:', roleErr);
            showSnackbar(
              hasPropertyChanges 
                ? 'Permission updated but failed to update role assignments. Please check role assignments manually.' 
                : 'Failed to update role assignments. Please try again.',
              'warning'
            );
          }
        }
        
        showSnackbar('Permission updated successfully', 'success');
        loadPermissions(); // Reload the list
        setOpenModal(false);
      } else {
        // Create new permission
        const createData = mapUiToCreatePermissionApiModel(formData);
        const response = await permissionsService.createPermission(createData);
        
        if (response.succeeded) {
          showSnackbar('Permission created successfully', 'success');
          loadPermissions(); // Reload the list
          setOpenModal(false);
        } else {
          showSnackbar(response.errorDescription || 'Failed to create permission', 'error');
        }
      }
    } catch (err) {
      console.error('Error saving permission:', err);
      showSnackbar('Failed to save permission. Please try again.', 'error');
    }
  };

  const handleCancel = (): void => {
    setOpenModal(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="h4" gutterBottom>
        Manage Permissions
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTable
          data={permissions}
          columns={columns}
          // onAdd={hasPermission(PERMISSIONS.CREATE_PERMISSION) ? handleAdd : undefined}
          // onEdit={hasPermission(PERMISSIONS.EDIT_PERMISSION) ? handleEdit : undefined}
          // onDelete={hasPermission(PERMISSIONS.DELETE_PERMISSION) ? handleDelete : undefined}
          title="Permissions"
          searchFields={['name', 'description']}
        />
      )}

      <Dialog open={openModal} onClose={handleCancel} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPermission ? 'Edit Permission' : 'Add New Permission'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Permission Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
              placeholder="e.g., user.read, role.manage"
              helperText="Use dot notation: resource.action"
            />
            
            <TextField
              label="Description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="Describe what this permission allows"
            />
            
            
            <FormControl fullWidth>
              <InputLabel>Linked Roles</InputLabel>
              <Select
                multiple
                value={formData.roles}
                onChange={(e) => setFormData({ ...formData, roles: e.target.value as string[] })}
                input={<OutlinedInput label="Linked Roles" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((roleId) => (
                      <Chip key={roleId} label={getRoleName(roleId)} size="small" color="primary" variant="outlined" />
                    ))}
                  </Box>
                )}
              >
                {roles.map((role) => (
                  <MenuItem key={role.id} value={role.id}>
                    <Checkbox checked={formData.roles.indexOf(role.id) > -1} />
                    <ListItemText primary={role.name} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
      
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Permissions; 